// In-depth React lessons (Codeazy lesson standard).

export const reactLessons = [
  {
    slug: "components-and-props",
    title: "Components, JSX and Props",
    summary: "Think in components: how JSX works, passing data with props, children and composition.",
    readTime: 13,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "Every React app is a tree of components. How you split a UI into components, and how data flows between them through props, decides whether your app is easy to change or a tangle. This is the foundation for everything else in React." },

      { type: "h2", text: "The mental model: LEGO bricks and recipes" },
      { type: "p", text: "A component is a **recipe** that describes a piece of UI. Props are the **ingredients** you pass in. The same recipe with different ingredients gives different dishes: `<CourseCard title=\"DSA\" />` and `<CourseCard title=\"System Design\" />`. Small recipes snap together like LEGO bricks to build pages." },
      { type: "callout", variant: "note", title: "The core idea", text: "UI = f(data). A component is a function that takes data (props and state) and returns a description of what the UI should look like. React handles updating the real DOM to match." },

      { type: "h2", text: "JSX is JavaScript" },
      { type: "p", text: "JSX looks like HTML, but it compiles to function calls that create plain objects describing the UI:" },
      { type: "compare", badLabel: "What you write (JSX)", bad: `<h1 className="title">Hello, {name}</h1>`, goodLabel: "What it becomes", good: `jsx("h1", {
  className: "title",
  children: ["Hello, ", name]
});
// → { type: "h1", props: { className: "title", children: [...] } }` },
      { type: "table", headers: ["JSX rule", "Example", "Why"], rows: [
        ["Use `className`, not `class`", "`<div className=\"card\">`", "`class` is a reserved word in JavaScript"],
        ["Use `htmlFor` on labels", "`<label htmlFor=\"email\">`", "`for` is also reserved"],
        ["camelCase attributes", "`onClick`, `tabIndex`, `strokeWidth`", "They map to DOM properties"],
        ["`{}` embeds any expression", "`{price * qty}`, `{user.name}`", "Not statements: no `if` or `for` inside braces"],
        ["`style` takes an object", "`style={{ marginTop: 8 }}`", "Outer braces for the expression, inner braces for the object"],
        ["Return one root element", "Wrap siblings in `<>...</>`", "A function returns a single value"],
        ["Close every tag", "`<img />`, `<br />`", "JSX is stricter than HTML"]
      ] },

      { type: "h2", text: "Your first components" },
      { type: "code", language: "jsx", code: `function CourseCard({ title, duration, level = "Beginner" }) {
  return (
    <article className="card">
      <span className="badge">{level}</span>
      <h3>{title}</h3>
      <p>{duration}</p>
    </article>
  );
}

function CourseList() {
  return (
    <section>
      <CourseCard title="Full-Stack" duration="6 months" />
      <CourseCard title="DSA" duration="4 months" level="All levels" />
    </section>
  );
}` },
      { type: "ul", items: [
        "Component names **must start with a capital letter**. `<courseCard>` would be treated as an HTML tag.",
        "Destructure props in the parameter list for readability, and set default values there.",
        "Keep components **pure**: the same props should always produce the same output, with no side effects during render."
      ] },

      { type: "h2", text: "Props in depth" },
      { type: "code", language: "jsx", code: `<CourseCard
  title="System Design"          // string
  modules={10}                   // number: use braces
  isNew                          // boolean shorthand for isNew={true}
  tags={["HLD", "LLD"]}          // array
  mentor={{ name: "Ravi" }}      // object
  onEnroll={() => enroll("sd")}  // function
/>` },
      { type: "h3", text: "Props are read-only" },
      { type: "compare", badLabel: "Mutating props", bad: `function Price({ amount }) {
  amount = amount * 0.9;   // don't reassign props
  return <p>₹{amount}</p>;
}`, goodLabel: "Derive a new value", good: `function Price({ amount, discount = 0 }) {
  const finalAmount = amount * (1 - discount);
  return <p>₹{finalAmount}</p>;
}` },
      { type: "h3", text: "Passing functions down, events up" },
      { type: "p", text: "Data flows **down** through props. To let a child tell its parent that something happened, the parent passes a function down and the child calls it:" },
      { type: "code", language: "jsx", code: `function EnrollButton({ courseId, onEnroll }) {
  return <button onClick={() => onEnroll(courseId)}>Enroll</button>;
}

function CoursePage() {
  const handleEnroll = (id) => console.log("Enrolling in", id);
  return <EnrollButton courseId="dsa" onEnroll={handleEnroll} />;
}`, output: `Enrolling in dsa   (when clicked)` },

      { type: "h2", text: "children and composition" },
      { type: "p", text: "Whatever you put between a component’s tags arrives as the `children` prop. This lets you build flexible wrappers such as cards, modals and layouts:" },
      { type: "code", language: "jsx", code: `function Card({ title, children, footer }) {
  return (
    <div className="card">
      <h3>{title}</h3>
      <div className="card__body">{children}</div>
      {footer && <div className="card__footer">{footer}</div>}
    </div>
  );
}

<Card title="Agentic AI" footer={<button>Enroll</button>}>
  <p>Build AI agents that reason and use tools.</p>
  <ul><li>LLMs</li><li>RAG</li></ul>
</Card>` },
      { type: "callout", variant: "tip", text: "Composition, meaning passing components as `children` or as props like `footer`, is React’s answer to inheritance. You almost never need class inheritance between components." },

      { type: "h2", text: "How to split a UI into components" },
      { type: "ol", items: [
        "Draw boxes around each piece of the design, and name them by what they are: `Navbar`, `CourseCard`, `PriceTag`.",
        "Apply the **single responsibility** idea: if a component does two unrelated things, split it.",
        "Look for repetition. Anything rendered in a list is usually its own component.",
        "Don’t over-split. A 20-line component with one job is fine, and dozens of tiny files can be harder to follow."
      ] },

      { type: "h2", text: "Common mistakes" },
      { type: "compare", badLabel: "Calling instead of passing", bad: `<button onClick={handleClick()}>
  Save
</button>
// runs on every render, not on click`, goodLabel: "Pass the function", good: `<button onClick={handleClick}>Save</button>

// with an argument:
<button onClick={() => handleDelete(id)}>
  Delete
</button>` },
      { type: "compare", badLabel: "Component defined inside a component", bad: `function Page() {
  function Item() { return <li>...</li>; }
  return <ul><Item /></ul>;
}
// Item is a NEW component every render,
// so its state resets each time`, goodLabel: "Define at the top level", good: `function Item() { return <li>...</li>; }

function Page() {
  return <ul><Item /></ul>;
}` },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "What is JSX?", answer: ["A syntax extension that lets you write UI as HTML-like markup inside JavaScript. A compiler such as Babel or SWC turns it into function calls (`jsx()` or `React.createElement`) that return plain objects describing elements. It isn’t required, but it makes components much easier to read."] },
      { type: "qa", question: "What is the difference between props and state?", answer: ["Props are inputs passed from a parent and are read-only inside the component. State is data the component owns and can change with its setter, which triggers a re-render. A parent’s state often becomes a child’s props."] },
      { type: "qa", question: "What does “one-way data flow” mean?", answer: ["Data moves down the tree from parents to children through props. Children communicate upwards by calling functions passed down as props. This makes it clear where data comes from and how it changes."] },
      { type: "qa", question: "What is the `children` prop?", answer: ["The content placed between a component’s opening and closing tags. It enables composition: generic wrappers like `Card`, `Modal` or `Layout` that render whatever is passed inside them."] },
      { type: "qa", question: "Why must component names be capitalised?", answer: ["JSX treats lowercase tags as built-in DOM elements (`\"div\"`) and capitalised tags as variables referring to components. `<card />` would try to create an unknown HTML element called `card`."] },
      { type: "qa", question: "What is a pure component?", answer: ["A component that always returns the same output for the same props and state, and does nothing else during render: no API calls, no mutating external variables and no DOM manipulation. Side effects belong in event handlers or effects."] },

      { type: "exercise", title: "Build a reusable Badge and ProfileCard", task: "Create a `Badge` component that takes `label` and an optional `tone` (\"info\" by default) and renders a span with class `badge badge--{tone}`. Then build a `ProfileCard` that takes `name`, `role`, `skills` (an array) and `children`, shows each skill as a `Badge`, and renders `children` below.", language: "jsx", solution: `function Badge({ label, tone = "info" }) {
  return <span className={"badge badge--" + tone}>{label}</span>;
}

function ProfileCard({ name, role, skills = [], children }) {
  return (
    <article className="profile-card">
      <h3>{name}</h3>
      <p>{role}</p>
      <div className="profile-card__skills">
        {skills.map((skill) => (
          <Badge key={skill} label={skill} />
        ))}
      </div>
      {children}
    </article>
  );
}

// Usage
<ProfileCard name="Asha" role="Frontend Developer" skills={["React", "CSS"]}>
  <button>View portfolio</button>
</ProfileCard>`, explanation: "`Badge` uses a default prop value. `ProfileCard` composes `Badge` and leaves room for any extra content through `children`. `skills = []` protects against a missing prop crashing on `.map`." },

      { type: "summary", items: [
        "Components are pure functions: data in, UI description out.",
        "JSX compiles to function calls. Use `className`, camelCase events and `{}` for expressions.",
        "Props flow down and are read-only, and callbacks let children report events up.",
        "Use `children` and component props for composition instead of inheritance.",
        "Pass functions to `onClick`, don’t call them, and define components at the top level."
      ] }
    ]
  },

  {
    slug: "state",
    title: "State and Rendering",
    summary: "useState, how re-renders actually work, immutability, batching and where state should live.",
    readTime: 14,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "State is what makes a UI interactive. Most React bugs, such as a UI that doesn’t update, values that are one step behind or too many re-renders, come from misunderstanding how state and rendering work. Get this right and React becomes predictable." },

      { type: "h2", text: "The mental model: snapshots" },
      { type: "p", text: "Every render is a **photograph** of your component at one moment. The props, the state values and the event handlers created during that render are all frozen inside that snapshot. Calling `setState` doesn’t change the current photo. It asks React to take a **new** one with the new value." },
      { type: "code", language: "jsx", code: `function Counter() {
  const [count, setCount] = useState(0);

  function handleClick() {
    setCount(count + 1);
    console.log(count);   // what does this log after the first click?
  }

  return <button onClick={handleClick}>{count}</button>;
}`, output: `0   ← the snapshot still has count = 0; the next render will have 1` },

      { type: "h2", text: "What triggers a render?" },
      { type: "ol", items: [
        "The **initial render**, when the component first appears.",
        "Its **state changes** through a setter function.",
        "Its **parent re-renders**. By default, children re-render with their parent.",
        "A **context** it uses changes."
      ] },
      { type: "p", text: "Rendering means calling your component function to get a new description. React then compares it with the previous one (**reconciliation**) and updates only the DOM nodes that actually changed. A re-render doesn’t automatically mean the DOM changed." },

      { type: "h2", text: "useState basics" },
      { type: "code", language: "jsx", code: `const [value, setValue] = useState(initialValue);

// Lazy initial state: the function runs only on the first render
const [cart, setCart] = useState(() => {
  const saved = localStorage.getItem("cart");
  return saved ? JSON.parse(saved) : [];
});` },

      { type: "h2", text: "Batching and functional updates" },
      { type: "p", text: "React batches several state updates in the same event into one re-render. Because each call reads from the same snapshot, this surprises many people:" },
      { type: "compare", badLabel: "Adds 1, not 3", bad: `function handleClick() {
  setCount(count + 1); // 0 + 1
  setCount(count + 1); // 0 + 1
  setCount(count + 1); // 0 + 1
}`, goodLabel: "Adds 3", good: `function handleClick() {
  setCount((c) => c + 1); // 0 → 1
  setCount((c) => c + 1); // 1 → 2
  setCount((c) => c + 1); // 2 → 3
}`, text: "An updater function receives the **latest** pending value. Use it whenever the new state depends on the previous state." },

      { type: "h2", text: "Immutability: never mutate state" },
      { type: "p", text: "React decides whether to re-render by comparing the old and new values with `Object.is`. If you mutate an object or array in place, it is still the same reference, so React may skip the update:" },
      { type: "compare", badLabel: "Mutation: UI doesn’t update", bad: `const [todos, setTodos] = useState([]);

function addTodo(text) {
  todos.push({ id: Date.now(), text });
  setTodos(todos); // same array reference
}`, goodLabel: "New array", good: `function addTodo(text) {
  setTodos((prev) => [
    ...prev,
    { id: Date.now(), text }
  ]);
}` },
      { type: "table", headers: ["Operation", "Immutable pattern"], rows: [
        ["Add to an array", "`[...items, newItem]`"],
        ["Remove from an array", "`items.filter((i) => i.id !== id)`"],
        ["Update an item", "`items.map((i) => i.id === id ? { ...i, done: true } : i)`"],
        ["Insert at an index", "`[...items.slice(0, n), newItem, ...items.slice(n)]`"],
        ["Update an object field", "`{ ...user, name: \"Ravi\" }`"],
        ["Update a nested field", "`{ ...user, address: { ...user.address, city: \"Pune\" } }`"],
        ["Sort without mutating", "`[...items].sort(compare)` or `items.toSorted(compare)`"]
      ] },
      { type: "callout", variant: "tip", text: "If deeply nested updates get painful, flatten the state shape or use a helper such as Immer, which lets you write mutating-style code that produces immutable updates." },

      { type: "h2", text: "Where should state live?" },
      { type: "p", text: "Put state in the **closest common parent** of all the components that need it. This is called **lifting state up**:" },
      { type: "code", language: "jsx", code: `function CoursePage() {
  const [query, setQuery] = useState("");          // shared by both children

  return (
    <>
      <SearchBox value={query} onChange={setQuery} />
      <CourseResults query={query} />
    </>
  );
}

function SearchBox({ value, onChange }) {
  return <input value={value} onChange={(e) => onChange(e.target.value)} />;
}

function CourseResults({ query }) {
  const visible = courses.filter((c) =>
    c.title.toLowerCase().includes(query.toLowerCase())
  );
  return <ul>{visible.map((c) => <li key={c.id}>{c.title}</li>)}</ul>;
}` },

      { type: "h2", text: "Don’t store what you can calculate" },
      { type: "compare", badLabel: "Redundant state", bad: `const [items, setItems] = useState([]);
const [total, setTotal] = useState(0);
// must remember to update total
// every time items change, and it will drift`, goodLabel: "Derived during render", good: `const [items, setItems] = useState([]);
const total = items.reduce(
  (sum, i) => sum + i.price * i.qty, 0
);` },
      { type: "ul", items: [
        "Can it be computed from props or other state? Then don’t store it.",
        "Does it never change? It is a constant, not state.",
        "Does it change but not affect what is shown, such as a timer ID? Use `useRef`."
      ] },

      { type: "h2", text: "useRef: values that don’t trigger renders" },
      { type: "code", language: "jsx", code: `function SearchInput() {
  const inputRef = useRef(null);       // reference to a DOM node
  const renderCount = useRef(0);       // mutable value, no re-render

  renderCount.current += 1;

  return (
    <>
      <input ref={inputRef} />
      <button onClick={() => inputRef.current.focus()}>Focus search</button>
    </>
  );
}` },
      { type: "table", headers: ["", "useState", "useRef"], rows: [
        ["Changing it re-renders", "Yes", "No"],
        ["Value during render", "The snapshot value", "Always the current `.current`"],
        ["Use for", "Anything shown in the UI", "DOM nodes, timer IDs, previous values, instance variables"]
      ] },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "Why doesn’t state update immediately after calling `setState`?", answer: ["Each render sees a fixed snapshot of state. `setState` schedules a re-render with the new value, but the variables in the current render don’t change. React also batches updates for performance. To compute from the latest value, use an updater function: `setCount((c) => c + 1)`."] },
      { type: "qa", question: "Why should state be treated as immutable?", answer: ["React compares state by reference with `Object.is`. Mutating an object or array keeps the same reference, so React may not re-render, and memoisation, `useEffect` dependencies and dev tools all rely on new references to detect change. Always create new objects and arrays."] },
      { type: "qa", question: "What is lifting state up?", answer: ["Moving state to the nearest common ancestor of the components that need it, then passing it down as props and passing setter callbacks for changes. It keeps one source of truth instead of several copies that can disagree."] },
      { type: "qa", question: "What is batching in React?", answer: ["Grouping several state updates into a single re-render. Since React 18, updates are batched automatically everywhere, including in promises, timeouts and native event handlers, not just React event handlers."] },
      { type: "qa", question: "When would you use `useRef` instead of `useState`?", answer: ["When a value must persist between renders but changing it shouldn’t update the UI, for example a DOM element reference, an interval ID, the previous value of a prop, or a flag that tracks whether a component is mounted."] },
      { type: "qa", question: "What is reconciliation?", answer: ["The process of comparing the new element tree from a render with the previous one and working out the minimal set of DOM changes. React assumes elements of different types produce different trees, and uses `key`s to match list items across renders."] },

      { type: "exercise", title: "Build a shopping cart reducer-style updater", task: "Using `useState`, build a `Cart` with: `addItem(product)`, which adds the product with `qty: 1` or increments `qty` if it is already in the cart; `removeItem(id)`; and a derived `total`. Never mutate state.", language: "jsx", solution: `function Cart() {
  const [items, setItems] = useState([]);

  const addItem = (product) => {
    setItems((prev) => {
      const exists = prev.some((i) => i.id === product.id);
      if (exists) {
        return prev.map((i) =>
          i.id === product.id ? { ...i, qty: i.qty + 1 } : i
        );
      }
      return [...prev, { ...product, qty: 1 }];
    });
  };

  const removeItem = (id) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  };

  const total = items.reduce((sum, i) => sum + i.price * i.qty, 0);

  return (
    <div>
      {items.map((i) => (
        <p key={i.id}>
          {i.title} × {i.qty}
          <button onClick={() => removeItem(i.id)}>Remove</button>
        </p>
      ))}
      <strong>Total: ₹{total}</strong>
    </div>
  );
}`, explanation: "Updater functions guarantee each change uses the latest cart. `map` with a spread creates a new object for the updated item, `filter` returns a new array, and the total is derived during render, so it can never get out of sync." },

      { type: "summary", items: [
        "Each render is a snapshot, and `setState` schedules a new render with new values.",
        "Use updater functions when the next state depends on the previous one.",
        "Never mutate state. Create new arrays and objects so React notices the change.",
        "Lift shared state to the closest common parent, and derive anything you can calculate.",
        "Use `useRef` for values that persist but shouldn’t re-render."
      ] }
    ]
  },

  {
    slug: "use-effect",
    title: "Effects and Data Fetching",
    summary: "useEffect done right: dependencies, cleanup, race conditions, and when you don’t need an effect at all.",
    readTime: 14,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "`useEffect` is the most misused hook. It causes infinite loops, stale data, race conditions and memory leaks when used wrongly, and many effects shouldn’t exist at all. Understanding what effects are for makes your components simpler and bug-free." },

      { type: "h2", text: "The mental model: synchronisation" },
      { type: "p", text: "Don’t think of effects as lifecycle events like “on mount”. Think of them as **keeping something outside React in sync with your component’s current props and state**. “Keep the chat connected to `roomId`.” “Keep the document title equal to the unread count.” When the values change, React re-syncs: it cleans up the old setup and runs the new one." },
      { type: "code", language: "jsx", code: `useEffect(() => {
  // 1. Setup: start syncing with the current values
  const connection = createConnection(roomId);
  connection.connect();

  // 2. Cleanup: stop syncing with the old values
  return () => connection.disconnect();
}, [roomId]); // 3. Re-sync whenever roomId changes` },

      { type: "h2", text: "The dependency array" },
      { type: "table", headers: ["Dependencies", "Effect runs", "Example"], rows: [
        ["None", "After every render", "Rarely what you want"],
        ["`[]`", "Once after the first render (twice in development Strict Mode)", "Subscribe to a global event"],
        ["`[a, b]`", "After the first render and whenever `a` or `b` changes", "Fetch when `userId` changes"]
      ] },
      { type: "callout", variant: "warning", text: "The dependency array isn’t a choice. It must list every reactive value (props, state and functions or objects defined in the component) that the effect uses. The `react-hooks/exhaustive-deps` lint rule enforces this. Don’t silence it; restructure the code instead." },

      { type: "h2", text: "Cleanup: preventing leaks" },
      { type: "code", language: "jsx", code: `function useWindowWidth() {
  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);   // cleanup
  }, []);

  return width;
}

function Countdown({ seconds }) {
  const [left, setLeft] = useState(seconds);

  useEffect(() => {
    const id = setInterval(() => setLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);                                  // cleanup
  }, []);

  return <p>{left}s</p>;
}` },
      { type: "p", text: "Cleanup runs **before the effect runs again** and **when the component unmounts**. In development, React’s Strict Mode deliberately mounts, unmounts and remounts components once to expose missing cleanups. That is why effects appear to run twice." },

      { type: "h2", text: "Fetching data correctly" },
      { type: "p", text: "A naive fetch has a **race condition**: if `userId` changes quickly from 1 to 2, the response for 1 might arrive last and overwrite the correct data:" },
      { type: "code", language: "jsx", code: `function Profile({ userId }) {
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState("loading");   // loading | success | error

  useEffect(() => {
    const controller = new AbortController();
    setStatus("loading");

    fetch("/api/users/" + userId, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) throw new Error("HTTP " + res.status);
        return res.json();
      })
      .then((data) => {
        setUser(data);
        setStatus("success");
      })
      .catch((err) => {
        if (err.name !== "AbortError") setStatus("error");
      });

    return () => controller.abort();   // cancel the stale request
  }, [userId]);

  if (status === "loading") return <Spinner />;
  if (status === "error") return <p>Couldn't load profile.</p>;
  return <h2>{user.name}</h2>;
}` },
      { type: "callout", variant: "tip", text: "In real apps, use a data-fetching library such as TanStack Query or SWR, or your framework’s loaders (Next.js, React Router). They handle caching, deduplication, retries, race conditions and background refresh, which are hard to get right by hand." },

      { type: "h2", text: "You might not need an effect" },
      { type: "p", text: "If there is no external system involved, you probably don’t need an effect:" },
      { type: "compare", badLabel: "Effect to derive data", bad: `const [items, setItems] = useState([]);
const [filtered, setFiltered] = useState([]);

useEffect(() => {
  setFiltered(items.filter((i) => i.active));
}, [items]);
// extra render + state that can drift`, goodLabel: "Calculate during render", good: `const [items, setItems] = useState([]);
const filtered = items.filter((i) => i.active);

// expensive? memoise it:
// const filtered = useMemo(
//   () => items.filter((i) => i.active), [items]);` },
      { type: "compare", badLabel: "Effect to respond to an event", bad: `const [submitted, setSubmitted] = useState(false);

useEffect(() => {
  if (submitted) {
    sendAnalytics("form_submit");
    showToast("Saved!");
  }
}, [submitted]);`, goodLabel: "Do it in the event handler", good: `function handleSubmit() {
  save(form);
  sendAnalytics("form_submit");
  showToast("Saved!");
}` },
      { type: "table", headers: ["Situation", "Use"], rows: [
        ["Transforming data for display", "Calculate during render (`useMemo` if expensive)"],
        ["Reacting to a user action", "The event handler"],
        ["Resetting state when a prop changes", "A `key` on the component: `<Profile key={userId} />`"],
        ["Syncing with the DOM, network, timers or subscriptions", "`useEffect` ✓"]
      ] },

      { type: "h2", text: "Infinite loops and how to avoid them" },
      { type: "compare", badLabel: "Infinite loop", bad: `const [data, setData] = useState([]);
const options = { page: 1 };   // new object every render

useEffect(() => {
  fetchData(options).then(setData);
}, [options]);  // always "changed"
// render → effect → setData → render → ...`, goodLabel: "Stable dependencies", good: `const [data, setData] = useState([]);
const [page, setPage] = useState(1);

useEffect(() => {
  fetchData({ page }).then(setData);
}, [page]);   // primitive, only changes on purpose` },

      { type: "h2", text: "useEffect vs useLayoutEffect" },
      { type: "p", text: "`useEffect` runs **after** the browser paints, so it doesn’t block the screen. `useLayoutEffect` runs **after DOM changes but before paint**. Use it only when you must measure the DOM and update before the user sees a flicker, for example to position a tooltip." },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "What is `useEffect` for?", answer: ["Synchronising a component with an external system, such as the network, browser APIs, timers, subscriptions or third-party widgets, based on the current props and state. It runs after rendering and can return a cleanup function."] },
      { type: "qa", question: "Why does my effect run twice in development?", answer: ["React Strict Mode intentionally mounts, unmounts and remounts components in development to reveal effects without proper cleanup. A correct effect with cleanup behaves the same either way. It doesn’t happen in production."] },
      { type: "qa", question: "How do you prevent race conditions when fetching in an effect?", answer: ["Cancel or ignore stale responses in the cleanup. Use an `AbortController` and call `abort()` in the cleanup, or set an `ignore` flag that the cleanup flips, and check it before calling `setState`. Data-fetching libraries handle this automatically."] },
      { type: "qa", question: "When should you not use `useEffect`?", answer: ["When transforming data for rendering (compute it during render), when handling user events (use the event handler), and when resetting state on a prop change (use a `key`). Effects are only for syncing with things outside React."] },
      { type: "qa", question: "What causes infinite re-render loops with effects?", answer: ["An effect that sets state on every run without a correct dependency array, or dependencies that change every render, such as objects, arrays or functions created during render. Fix them with primitive dependencies, `useMemo`/`useCallback` for stable references, or by moving the object inside the effect."] },
      { type: "qa", question: "What is the difference between `useEffect` and `useLayoutEffect`?", answer: ["`useEffect` runs asynchronously after paint, which is the default and doesn’t block the screen. `useLayoutEffect` runs synchronously after DOM mutations but before paint. Use it for DOM measurements that must be applied without a visible flicker."] },

      { type: "exercise", title: "Build a debounced search hook", task: "Write a `useDebouncedValue(value, delay)` hook that returns `value` only after it has stopped changing for `delay` milliseconds. Then use it to fetch search results only when the debounced query changes, cancelling stale requests.", language: "jsx", solution: `function useDebouncedValue(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);       // reset the timer on every change
  }, [value, delay]);

  return debounced;
}

function CourseSearch() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const debouncedQuery = useDebouncedValue(query, 400);

  useEffect(() => {
    if (!debouncedQuery) return setResults([]);
    const controller = new AbortController();

    fetch("/api/courses?q=" + encodeURIComponent(debouncedQuery), {
      signal: controller.signal
    })
      .then((res) => res.json())
      .then(setResults)
      .catch((err) => { if (err.name !== "AbortError") console.error(err); });

    return () => controller.abort();
  }, [debouncedQuery]);

  return (
    <>
      <input value={query} onChange={(e) => setQuery(e.target.value)} />
      <ul>{results.map((r) => <li key={r.id}>{r.title}</li>)}</ul>
    </>
  );
}`, explanation: "Each keystroke clears the previous timer through the cleanup, so `debounced` updates only after the user pauses. The fetch effect depends only on the debounced value, and its cleanup aborts any request made stale by a newer query." },

      { type: "summary", items: [
        "Effects synchronise React with external systems. Think “keep in sync”, not “on mount”.",
        "List every reactive value in the dependency array, and don’t silence the lint rule.",
        "Always clean up subscriptions, timers and requests. Strict Mode helps you catch missing cleanups.",
        "Prevent race conditions with `AbortController`, or use a data-fetching library.",
        "Derive data during render and handle user actions in event handlers. No effect is needed for those."
      ] }
    ]
  },

  {
    slug: "lists-and-conditional-rendering",
    title: "Lists, Keys and Conditional Rendering",
    summary: "Render dynamic lists efficiently, understand why keys matter, and show or hide UI cleanly.",
    readTime: 11,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "Almost every screen renders a list: courses, messages, products or search results. Wrong keys cause subtle bugs, such as inputs showing the wrong values or animations jumping, and messy conditionals make components hard to read. Both topics come up constantly in interviews." },

      { type: "h2", text: "Rendering lists with map" },
      { type: "code", language: "jsx", code: `const courses = [
  { id: "fs", title: "Full-Stack", level: "Beginner" },
  { id: "dsa", title: "DSA", level: "All levels" },
  { id: "sd", title: "System Design", level: "Intermediate" }
];

function CourseList() {
  return (
    <ul>
      {courses.map((course) => (
        <li key={course.id}>
          {course.title} <small>({course.level})</small>
        </li>
      ))}
    </ul>
  );
}` },
      { type: "p", text: "You can chain array methods before rendering:" },
      { type: "code", language: "jsx", code: `{courses
  .filter((c) => c.level !== "Advanced")
  .toSorted((a, b) => a.title.localeCompare(b.title))
  .map((c) => <CourseCard key={c.id} {...c} />)}` },

      { type: "h2", text: "The mental model for keys: name tags" },
      { type: "p", text: "Keys are **name tags** for list items. When the list changes, React uses the name tags to tell which item is which: who is new, who left and who moved. Without stable name tags, React matches items by position, like a teacher calling the register by seat number after students have swapped seats." },

      { type: "h3", text: "What goes wrong with index keys" },
      { type: "code", language: "jsx", code: `// Each row has an uncontrolled input
{todos.map((todo, index) => (
  <li key={index}>
    {todo.text} <input placeholder="notes" />
  </li>
))}` },
      { type: "ol", items: [
        "Type “buy milk” in the notes input of the first item (key 0).",
        "Add a new todo at the **top** of the list.",
        "The new item now has key 0, so React reuses the first row’s DOM and its input.",
        "The notes “buy milk” now appear next to the **new** item."
      ] },
      { type: "table", headers: ["Key choice", "Verdict"], rows: [
        ["A database ID: `key={course.id}`", "Best: stable and unique"],
        ["A unique natural value: `key={user.email}`", "Good if it never changes"],
        ["`key={index}`", "Only for static lists that never reorder, filter or insert"],
        ["`key={Math.random()}`", "Never: a new key every render remounts everything and loses state"]
      ] },
      { type: "callout", variant: "note", text: "Keys only need to be unique among siblings, not across the whole app. They aren’t passed as a prop, so if the child needs the ID, pass it separately: `<Item key={id} id={id} />`." },

      { type: "h3", text: "Keys can reset components on purpose" },
      { type: "code", language: "jsx", code: `// When userId changes, React throws away the old Profile
// and creates a fresh one, so all its internal state is reset.
<Profile key={userId} userId={userId} />` },

      { type: "h2", text: "Conditional rendering patterns" },
      { type: "code", language: "jsx", code: `function CourseStatus({ course, user }) {
  // 1. Early return for whole-component conditions
  if (!course) return <Skeleton />;

  return (
    <div>
      {/* 2. Ternary for either/or */}
      {user ? <p>Welcome back, {user.name}</p> : <a href="/login">Log in</a>}

      {/* 3. && for show/hide */}
      {course.isNew && <span className="badge">New</span>}

      {/* 4. Variable for complex logic */}
      {renderCta(course, user)}
    </div>
  );
}

function renderCta(course, user) {
  if (!user) return <button>Sign up to enroll</button>;
  if (user.enrolled.includes(course.id)) return <button>Continue learning</button>;
  if (course.seatsLeft === 0) return <button disabled>Batch full</button>;
  return <button>Enroll now</button>;
}` },
      { type: "h3", text: "The && zero trap" },
      { type: "compare", badLabel: "Renders a “0”", bad: `{cart.length && <CartBadge count={cart.length} />}
// when cart is empty, renders: 0`, goodLabel: "Use a boolean", good: `{cart.length > 0 && <CartBadge count={cart.length} />}`, text: "`&&` returns the left side when it is falsy. React doesn’t render `false`, `null` or `undefined`, but it **does** render the number 0." },
      { type: "h3", text: "Mapping states to UI" },
      { type: "code", language: "jsx", code: `const statusUi = {
  loading: <Spinner />,
  error: <ErrorMessage />,
  empty: <p>No courses found.</p>,
  success: <CourseGrid courses={courses} />
};

return statusUi[status];` },

      { type: "h2", text: "Large lists: virtualisation" },
      { type: "p", text: "Rendering 10,000 rows creates 10,000 DOM nodes and slows the page. **Virtualisation** renders only the rows currently visible, plus a small buffer, and swaps them as the user scrolls. Use a library such as TanStack Virtual or react-window when lists exceed a few hundred complex rows." },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "Why does React need keys in lists?", answer: ["Keys give each item a stable identity between renders, so React can match old and new items when the list is reordered, filtered or added to. React then preserves the right component state and DOM nodes and makes minimal updates. Without keys it matches by position, which causes state to attach to the wrong items."] },
      { type: "qa", question: "Is it okay to use the array index as a key?", answer: ["Only if the list is static: never reordered, filtered or added to at the start or middle. Otherwise index keys cause state and DOM to be reused for the wrong item, especially with inputs, and lead to unnecessary re-renders. Prefer stable IDs from the data."] },
      { type: "qa", question: "What does this render when `count` is 0: `{count && <Badge />}`?", answer: ["It renders `0`. The `&&` expression evaluates to `0` (the falsy left operand), and React renders numbers. Use `{count > 0 && <Badge />}` or a ternary."] },
      { type: "qa", question: "How can you reset a component’s state when a prop changes?", answer: ["Give it a `key` based on that prop, for example `<Chat key={roomId} />`. When the key changes, React unmounts the old instance and mounts a new one with fresh state, with no effect needed."] },
      { type: "qa", question: "How would you render a list of 50,000 items efficiently?", answer: ["Virtualise it so only visible rows plus a buffer are in the DOM, using a library such as TanStack Virtual. Also consider pagination or infinite scrolling, memoising row components and avoiding expensive work inside each row."] },

      { type: "exercise", title: "Filterable, sortable course list", task: "Build a `CourseBrowser` that takes `courses` (`{ id, title, level, rating }`) and renders level filter buttons (All, Beginner, Intermediate, Advanced), a sort dropdown (title or rating), a count of the results and an empty-state message. Use correct keys and derive everything during render.", language: "jsx", solution: `const LEVELS = ["All", "Beginner", "Intermediate", "Advanced"];

function CourseBrowser({ courses }) {
  const [level, setLevel] = useState("All");
  const [sortBy, setSortBy] = useState("title");

  const visible = courses
    .filter((c) => level === "All" || c.level === level)
    .toSorted((a, b) =>
      sortBy === "rating" ? b.rating - a.rating : a.title.localeCompare(b.title)
    );

  return (
    <section>
      <div role="group" aria-label="Filter by level">
        {LEVELS.map((l) => (
          <button key={l} aria-pressed={level === l} onClick={() => setLevel(l)}>
            {l}
          </button>
        ))}
      </div>

      <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
        <option value="title">Title (A–Z)</option>
        <option value="rating">Highest rated</option>
      </select>

      <p>{visible.length} course{visible.length === 1 ? "" : "s"}</p>

      {visible.length === 0 ? (
        <p>No courses at this level yet.</p>
      ) : (
        <ul>
          {visible.map((c) => (
            <li key={c.id}>{c.title} · ⭐ {c.rating}</li>
          ))}
        </ul>
      )}
    </section>
  );
}`, explanation: "Only the filter and sort choices are state. The visible list is derived during render, so it is always consistent. `toSorted` returns a new array, so the `courses` prop is never mutated. Course rows use `c.id` as the key, and the static filter buttons can safely use the label." },

      { type: "summary", items: [
        "Render lists with `map`, and chain `filter` and sort methods (without mutating) before it.",
        "Keys are stable name tags. Use IDs from your data, not indexes or random values.",
        "Changing a component’s `key` resets its state on purpose.",
        "Use early returns, ternaries, `&&` (with booleans!) and lookup objects for conditionals.",
        "Virtualise very long lists."
      ] }
    ]
  },

  {
    slug: "forms",
    title: "Forms in React",
    summary: "Controlled vs uncontrolled inputs, validation, submission states and building forms that scale.",
    readTime: 12,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "Login, sign-up, checkout, search and settings are all forms. Machine coding rounds frequently ask you to build one with validation. React gives you two approaches, and knowing when to use each keeps forms simple and fast." },

      { type: "h2", text: "The mental model: who owns the value?" },
      { type: "p", text: "In a **controlled** input, React state is the single source of truth: the input displays state, and every keystroke updates state. In an **uncontrolled** input, the DOM keeps the value, and you read it when you need it, usually on submit." },
      { type: "table", headers: ["", "Controlled", "Uncontrolled"], rows: [
        ["Value lives in", "React state", "The DOM"],
        ["Read the value", "Any time, from state", "On submit, via `FormData` or a ref"],
        ["Re-renders per keystroke", "Yes", "No"],
        ["Best for", "Live validation, dependent fields, formatting as you type", "Simple forms, large forms, file inputs"]
      ] },

      { type: "h2", text: "Controlled inputs" },
      { type: "code", language: "jsx", code: `function SignupForm() {
  const [form, setForm] = useState({ name: "", email: "", batch: "weekday", agree: false });

  // One handler for every field, using the input's name attribute
  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  }

  return (
    <form>
      <input name="name" value={form.name} onChange={handleChange} />
      <input name="email" type="email" value={form.email} onChange={handleChange} />
      <select name="batch" value={form.batch} onChange={handleChange}>
        <option value="weekday">Weekday</option>
        <option value="weekend">Weekend</option>
      </select>
      <label>
        <input name="agree" type="checkbox" checked={form.agree} onChange={handleChange} />
        I agree to the terms
      </label>
    </form>
  );
}` },
      { type: "callout", variant: "warning", text: "A `value` without an `onChange` makes the input read-only, and React will warn you. Also, never switch an input between `undefined` and a string. Initialise state with `\"\"`, not `undefined`." },

      { type: "h2", text: "Uncontrolled inputs with FormData" },
      { type: "code", language: "jsx", code: `function ContactForm() {
  function handleSubmit(e) {
    e.preventDefault();
    const data = Object.fromEntries(new FormData(e.currentTarget));
    console.log(data);   // { name: "Asha", message: "Hi!" }
  }

  return (
    <form onSubmit={handleSubmit}>
      <input name="name" defaultValue="" required />
      <textarea name="message" required />
      <button type="submit">Send</button>
    </form>
  );
}` },

      { type: "h2", text: "Validation and submission states" },
      { type: "code", language: "jsx", code: `function validate(values) {
  const errors = {};
  if (!values.name.trim()) errors.name = "Name is required";
  if (!/^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$/.test(values.email)) errors.email = "Enter a valid email";
  if (values.password.length < 8) errors.password = "Use at least 8 characters";
  return errors;
}

function RegisterForm() {
  const [values, setValues] = useState({ name: "", email: "", password: "" });
  const [touched, setTouched] = useState({});
  const [status, setStatus] = useState("idle");   // idle | submitting | success | error

  const errors = validate(values);                // derived, not stored
  const showError = (field) => touched[field] && errors[field];

  const handleChange = (e) => setValues((v) => ({ ...v, [e.target.name]: e.target.value }));
  const handleBlur = (e) => setTouched((t) => ({ ...t, [e.target.name]: true }));

  async function handleSubmit(e) {
    e.preventDefault();
    setTouched({ name: true, email: true, password: true });
    if (Object.keys(errors).length > 0) return;

    setStatus("submitting");
    try {
      const res = await fetch("/api/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values)
      });
      if (!res.ok) throw new Error();
      setStatus("success");
    } catch {
      setStatus("error");
    }
  }

  if (status === "success") return <p role="status">Account created! Check your email.</p>;

  return (
    <form onSubmit={handleSubmit} noValidate>
      <label htmlFor="email">Email</label>
      <input id="email" name="email" value={values.email}
             onChange={handleChange} onBlur={handleBlur}
             aria-invalid={Boolean(showError("email"))}
             aria-describedby="email-error" />
      {showError("email") && <p id="email-error">{errors.email}</p>}

      {/* name and password fields follow the same pattern */}

      {status === "error" && <p role="alert">Something went wrong. Please try again.</p>}
      <button disabled={status === "submitting"}>
        {status === "submitting" ? "Creating account…" : "Create account"}
      </button>
    </form>
  );
}` },
      { type: "ul", items: [
        "Errors are **derived** from values, not stored separately.",
        "Errors appear only after a field is **touched** (blurred) or the form is submitted, not while the user is still typing.",
        "The submit button is disabled while submitting, which prevents double submissions.",
        "Success and error states are explicit, and accessible roles announce them."
      ] },

      { type: "h2", text: "Scaling up: form libraries" },
      { type: "p", text: "For large forms, **React Hook Form** (fast, mostly uncontrolled) combined with a schema validator such as **Zod** removes most of the boilerplate. The same schema can be shared with the backend:" },
      { type: "code", language: "jsx", code: `const schema = z.object({
  email: z.string().email("Enter a valid email"),
  password: z.string().min(8, "Use at least 8 characters")
});

function LoginForm() {
  const { register, handleSubmit, formState: { errors, isSubmitting } } =
    useForm({ resolver: zodResolver(schema) });

  return (
    <form onSubmit={handleSubmit(login)}>
      <input {...register("email")} />
      {errors.email && <p>{errors.email.message}</p>}
      <input type="password" {...register("password")} />
      {errors.password && <p>{errors.password.message}</p>}
      <button disabled={isSubmitting}>Log in</button>
    </form>
  );
}` },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "What is the difference between controlled and uncontrolled components?", answer: ["A controlled input gets its value from React state and reports changes through `onChange`, so React is the source of truth. An uncontrolled input keeps its own value in the DOM, set initially with `defaultValue`, and you read it with a ref or `FormData`. Controlled inputs suit live validation and dependent fields, and uncontrolled ones suit simple or very large forms."] },
      { type: "qa", question: "How do you handle many inputs without writing a handler for each?", answer: ["Give each input a `name` matching a key in a state object, and use one handler: `setForm(prev => ({ ...prev, [e.target.name]: e.target.value }))`, handling `checked` for checkboxes."] },
      { type: "qa", question: "How do you prevent a form from being submitted twice?", answer: ["Track a submitting status, disable the submit button while it is true, and ignore submissions while a request is in flight. The server should also be idempotent where possible, for example using idempotency keys for payments."] },
      { type: "qa", question: "Why use a library like React Hook Form?", answer: ["It reduces boilerplate for registration, validation, errors and touched states. It minimises re-renders using uncontrolled inputs, integrates with schema validators such as Zod or Yup, and handles complex cases like field arrays."] },

      { type: "exercise", title: "OTP input", task: "Build a 6-digit OTP input: six single-character controlled inputs. Typing a digit moves focus to the next box, Backspace on an empty box moves to the previous one, non-digits are ignored, and an `onComplete(code)` callback fires when all six are filled.", language: "jsx", solution: `function OtpInput({ length = 6, onComplete }) {
  const [digits, setDigits] = useState(Array(length).fill(""));
  const inputs = useRef([]);

  function handleChange(index, value) {
    if (!/^\\d?$/.test(value)) return;            // allow one digit or empty
    const next = digits.map((d, i) => (i === index ? value : d));
    setDigits(next);
    if (value && index < length - 1) inputs.current[index + 1].focus();
    if (next.every(Boolean)) onComplete(next.join(""));
  }

  function handleKeyDown(index, e) {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputs.current[index - 1].focus();
    }
  }

  return (
    <div role="group" aria-label="One-time password">
      {digits.map((digit, i) => (
        <input
          key={i}
          ref={(el) => (inputs.current[i] = el)}
          value={digit}
          inputMode="numeric"
          autoComplete={i === 0 ? "one-time-code" : "off"}
          maxLength={1}
          aria-label={"Digit " + (i + 1)}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
        />
      ))}
    </div>
  );
}`, explanation: "State holds an array of digits, and a ref array holds the DOM nodes for moving focus. The new array is computed once and used for both the state update and the completion check, so it never reads stale state. Index keys are fine here because the boxes never reorder." },

      { type: "summary", items: [
        "Controlled inputs keep the value in state, and uncontrolled inputs keep it in the DOM.",
        "Use a single `name`-based change handler for multi-field forms.",
        "Derive errors, show them after a field is touched or on submit, and link them with `aria-describedby`.",
        "Model submission as idle, submitting, success or error, and disable the button while submitting.",
        "For big forms, use React Hook Form with Zod."
      ] }
    ]
  },

  {
    slug: "context-and-custom-hooks",
    title: "Context, useReducer and Custom Hooks",
    summary: "Share state without prop drilling, manage complex state transitions, and extract reusable logic.",
    readTime: 13,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "As apps grow, you need to share data such as the logged-in user, the theme or the cart across many components, handle state with many transitions, and reuse logic between components. Context, `useReducer` and custom hooks are React’s built-in tools for all three." },

      { type: "h2", text: "The problem: prop drilling" },
      { type: "code", language: "jsx", code: `<App user={user}>
  <Layout user={user}>              // doesn't use user
    <Sidebar user={user}>           // doesn't use user
      <ProfileMenu user={user} />   // finally uses it
    </Sidebar>
  </Layout>
</App>` },

      { type: "h2", text: "Context: a broadcast channel" },
      { type: "p", text: "Think of Context as a **radio station**. A Provider broadcasts a value, and any component below it can tune in with `useContext`, however deep it is, without the value being passed through every layer in between." },
      { type: "code", language: "jsx", code: `// 1. Create the context
const AuthContext = createContext(null);

// 2. Provide it near the top of the tree
function AuthProvider({ children }) {
  const [user, setUser] = useState(null);

  const value = useMemo(
    () => ({
      user,
      login: (u) => setUser(u),
      logout: () => setUser(null)
    }),
    [user]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// 3. A custom hook to consume it safely
function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

// 4. Use it anywhere below the provider
function ProfileMenu() {
  const { user, logout } = useAuth();
  return user ? <button onClick={logout}>Log out {user.name}</button> : null;
}` },
      { type: "h3", text: "Context and re-renders" },
      { type: "p", text: "**Every** component that reads a context re-renders when the provider’s `value` changes. Two habits keep this cheap:" },
      { type: "ul", items: [
        "Memoise the provider value with `useMemo`, otherwise a new object is created on every render and every consumer re-renders.",
        "Split unrelated data into separate contexts, such as `ThemeContext`, `AuthContext` and `CartContext`, so a cart change doesn’t re-render theme consumers."
      ] },
      { type: "table", headers: ["Good for Context", "Better elsewhere"], rows: [
        ["Theme, locale, the authenticated user, feature flags", "Server data (use TanStack Query or SWR)"],
        ["Rarely changing, widely needed values", "Rapidly changing state used by many components (use Zustand, Redux Toolkit or Jotai)"],
        ["Dependency injection for a subtree", "Data used by one or two nearby components (just pass props)"]
      ] },

      { type: "h2", text: "useReducer: state machines for complex state" },
      { type: "p", text: "When state has many related fields and transitions, scattered `setState` calls get hard to follow. A reducer puts **all transitions in one place**: you dispatch **what happened** (an action), and the reducer decides **how state changes**." },
      { type: "code", language: "jsx", code: `const initialState = { items: [], coupon: null };

function cartReducer(state, action) {
  switch (action.type) {
    case "added": {
      const existing = state.items.find((i) => i.id === action.product.id);
      const items = existing
        ? state.items.map((i) => i.id === action.product.id ? { ...i, qty: i.qty + 1 } : i)
        : [...state.items, { ...action.product, qty: 1 }];
      return { ...state, items };
    }
    case "removed":
      return { ...state, items: state.items.filter((i) => i.id !== action.id) };
    case "couponApplied":
      return { ...state, coupon: action.code };
    case "cleared":
      return initialState;
    default:
      throw new Error("Unknown action: " + action.type);
  }
}

function Cart() {
  const [state, dispatch] = useReducer(cartReducer, initialState);
  return (
    <button onClick={() => dispatch({ type: "added", product: { id: "dsa", title: "DSA", price: 4999 } })}>
      Add DSA
    </button>
  );
}` },
      { type: "table", headers: ["", "useState", "useReducer"], rows: [
        ["Best for", "Independent, simple values", "Related values with many transitions"],
        ["Update logic lives", "Spread across event handlers", "In one pure function"],
        ["Testing", "Through the component", "Test the reducer as a plain function"],
        ["Debugging", "“Where did this change?”", "Log each action and the resulting state"]
      ] },
      { type: "callout", variant: "tip", text: "Context plus useReducer gives you lightweight global state: put `state` and `dispatch` in context. `dispatch` never changes identity, so components that only dispatch don’t need to re-render when state changes, if you provide it in its own context." },

      { type: "h2", text: "Custom hooks: reusable stateful logic" },
      { type: "p", text: "A custom hook is a function whose name starts with `use` and which calls other hooks. It shares **logic**, not state: every component that calls it gets its own independent state." },
      { type: "code", language: "jsx", code: `function useLocalStorage(key, initialValue) {
  const [value, setValue] = useState(() => {
    try {
      const saved = localStorage.getItem(key);
      return saved !== null ? JSON.parse(saved) : initialValue;
    } catch {
      return initialValue;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // storage full or blocked: keep working in memory
    }
  }, [key, value]);

  return [value, setValue];
}

// Usage: same API as useState, but persisted
const [theme, setTheme] = useLocalStorage("theme", "dark");` },
      { type: "code", language: "jsx", code: `function useToggle(initial = false) {
  const [on, setOn] = useState(initial);
  const toggle = useCallback(() => setOn((v) => !v), []);
  return [on, toggle];
}

function useOnlineStatus() {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);
  return online;
}` },
      { type: "h3", text: "Rules of hooks" },
      { type: "ol", items: [
        "Call hooks only at the **top level**: not inside loops, conditions or nested functions. React tracks hooks by call order.",
        "Call hooks only from React components or other custom hooks.",
        "Name custom hooks starting with `use` so the linter can check these rules."
      ] },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "What is prop drilling and how do you avoid it?", answer: ["Passing props through intermediate components that don’t use them, just to reach a deep child. You can avoid it with composition (passing components as children), Context for widely shared values, or a state library for complex global state."] },
      { type: "qa", question: "What are the performance pitfalls of Context?", answer: ["All consumers re-render whenever the provider value changes identity. Creating the value object inline causes re-renders on every provider render. Memoise the value, split contexts by concern, and keep fast-changing state out of widely consumed contexts."] },
      { type: "qa", question: "When would you choose `useReducer` over `useState`?", answer: ["When state has several related fields, the next state depends on the previous one in complex ways, or many event types change the same state. Reducers centralise transitions in a pure, testable function and make state changes explicit through actions."] },
      { type: "qa", question: "What is a custom hook, and do two components using it share state?", answer: ["A function starting with `use` that composes other hooks to package reusable logic. Each call has its own independent state. Components don’t share state through a custom hook unless the hook reads from a shared source such as context or an external store."] },
      { type: "qa", question: "Why can’t hooks be called conditionally?", answer: ["React identifies each hook’s state by the order in which hooks are called during render. A conditional call would shift that order between renders and connect state to the wrong hook. Put the condition inside the hook’s logic instead."] },
      { type: "qa", question: "Context vs Redux/Zustand: when do you need a state library?", answer: ["Context is a way to pass values down, not a state manager. When many components read and update frequently changing global state, need selective subscriptions to avoid re-renders, or you want devtools and middleware, a store such as Zustand or Redux Toolkit is a better fit."] },

      { type: "exercise", title: "Build a ThemeProvider with a custom hook", task: "Create a `ThemeProvider` that stores \"light\" or \"dark\" in localStorage (reuse `useLocalStorage`), applies it as `data-theme` on `document.documentElement`, and exposes `{ theme, toggleTheme }` through a `useTheme()` hook that throws if used outside the provider.", language: "jsx", solution: `const ThemeContext = createContext(null);

function ThemeProvider({ children }) {
  const [theme, setTheme] = useLocalStorage("theme", "dark");

  useEffect(() => {
    document.documentElement.dataset.theme = theme;   // sync with the DOM
  }, [theme]);

  const value = useMemo(
    () => ({
      theme,
      toggleTheme: () => setTheme((t) => (t === "dark" ? "light" : "dark"))
    }),
    [theme, setTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside <ThemeProvider>");
  return ctx;
}

function ThemeToggle() {
  const { theme, toggleTheme } = useTheme();
  return <button onClick={toggleTheme}>Switch to {theme === "dark" ? "light" : "dark"}</button>;
}`, explanation: "The custom hook `useLocalStorage` handles persistence, an effect syncs the theme to the DOM (an external system), `useMemo` keeps the context value stable, and `useTheme` gives consumers a safe, descriptive API." },

      { type: "summary", items: [
        "Context broadcasts values to a subtree, which avoids prop drilling.",
        "Memoise provider values and split contexts by concern to avoid wasted re-renders.",
        "`useReducer` centralises complex state transitions in a pure, testable function.",
        "Custom hooks share logic, not state, and each caller gets its own state.",
        "Follow the rules of hooks: top level only, and only in components or hooks."
      ] }
    ]
  },

  {
    slug: "performance",
    title: "Performance and Memoisation",
    summary: "Find real bottlenecks, then fix them with React.memo, useMemo, useCallback, code splitting and transitions.",
    readTime: 13,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "React is fast by default, but large lists, heavy calculations and careless re-renders can make apps sluggish, especially on budget phones. Knowing how to measure first and then apply the right fix is a senior-level skill that interviewers probe." },

      { type: "h2", text: "The mental model: measure, then cut" },
      { type: "p", text: "Performance work is like a doctor’s visit: **diagnose before prescribing**. Wrapping everything in `useMemo` is like taking every medicine in the cabinet: it adds cost and rarely helps. First find the slow part with the React DevTools Profiler, then apply the specific fix." },
      { type: "ol", items: [
        "Reproduce the slowness with a production build. Development mode is much slower.",
        "Record an interaction in the **React DevTools Profiler**.",
        "Look for components that render often or take a long time (wide bars in the flame chart).",
        "Turn on “Highlight updates when components render” to see wasted renders.",
        "Fix the biggest problem, then measure again."
      ] },

      { type: "h2", text: "Fix #1: move state down" },
      { type: "p", text: "Often the best optimisation needs no memoisation at all. Keep fast-changing state as low in the tree as possible:" },
      { type: "compare", badLabel: "Whole page re-renders on each keystroke", bad: `function Page() {
  const [query, setQuery] = useState("");
  return (
    <>
      <input value={query}
             onChange={(e) => setQuery(e.target.value)} />
      <HeavyChart />      {/* re-renders on every key */}
      <HeavyTable />
    </>
  );
}`, goodLabel: "State isolated", good: `function SearchBox() {
  const [query, setQuery] = useState("");
  return <input value={query}
                onChange={(e) => setQuery(e.target.value)} />;
}

function Page() {
  return (
    <>
      <SearchBox />
      <HeavyChart />      {/* unaffected */}
      <HeavyTable />
    </>
  );
}` },

      { type: "h2", text: "Fix #2: React.memo" },
      { type: "p", text: "`memo` makes a component skip re-rendering when its props are shallowly equal to the previous props:" },
      { type: "code", language: "jsx", code: `const CourseRow = memo(function CourseRow({ course, onSelect }) {
  return <li onClick={() => onSelect(course.id)}>{course.title}</li>;
});` },
      { type: "callout", variant: "warning", text: "`memo` is useless if you pass a new object, array or function on every render. `{}` !== `{}` and `() => {}` !== `() => {}`, so the shallow comparison always fails. This is where `useMemo` and `useCallback` come in." },

      { type: "h2", text: "Fix #3: useMemo and useCallback" },
      { type: "table", headers: ["Hook", "Caches", "Use it to"], rows: [
        ["`useMemo(() => compute(a), [a])`", "A computed **value**", "Skip expensive calculations, and keep object or array props stable for memoised children"],
        ["`useCallback(fn, [deps])`", "A **function** reference", "Keep callback props stable for memoised children or effect dependencies"]
      ] },
      { type: "code", language: "jsx", code: `function CourseCatalog({ courses, query }) {
  const [selected, setSelected] = useState(null);

  // Expensive: filtering and sorting thousands of items
  const visible = useMemo(
    () => courses
      .filter((c) => c.title.toLowerCase().includes(query.toLowerCase()))
      .toSorted((a, b) => b.rating - a.rating),
    [courses, query]
  );

  // Stable function so memoised rows don't re-render
  const handleSelect = useCallback((id) => setSelected(id), []);

  return (
    <ul>
      {visible.map((c) => (
        <CourseRow key={c.id} course={c} onSelect={handleSelect} />
      ))}
    </ul>
  );
}` },
      { type: "h3", text: "When not to memoise" },
      { type: "ul", items: [
        "Cheap calculations. Filtering 20 items is faster than the bookkeeping `useMemo` adds.",
        "Components that rarely re-render, or always receive new props anyway.",
        "Callbacks passed only to plain DOM elements such as `<button onClick>`, which aren’t memoised."
      ] },
      { type: "callout", variant: "note", text: "The React Compiler, introduced in React 19, can automatically memoise components and values at build time. In codebases that use it, manual `useMemo`, `useCallback` and `memo` become rarely necessary. Understanding why they exist still matters for debugging and interviews." },

      { type: "h2", text: "Fix #4: keep the UI responsive with transitions" },
      { type: "p", text: "Some updates are urgent, such as typing, and some can wait, such as re-rendering a big filtered list. `useTransition` and `useDeferredValue` let React keep typing smooth while the heavy update catches up:" },
      { type: "code", language: "jsx", code: `function Search({ allCourses }) {
  const [query, setQuery] = useState("");
  const deferredQuery = useDeferredValue(query);   // lags behind while busy
  const isStale = query !== deferredQuery;

  return (
    <>
      <input value={query} onChange={(e) => setQuery(e.target.value)} />
      <div style={{ opacity: isStale ? 0.6 : 1 }}>
        <ResultsList query={deferredQuery} courses={allCourses} />
      </div>
    </>
  );
}

const ResultsList = memo(function ResultsList({ query, courses }) {
  const results = courses.filter((c) => c.title.includes(query));
  return results.map((c) => <CourseRow key={c.id} course={c} />);
});` },

      { type: "h2", text: "Fix #5: ship less JavaScript" },
      { type: "p", text: "The fastest code is code that isn’t downloaded. Split rarely used routes and heavy components into separate chunks that load on demand:" },
      { type: "code", language: "jsx", code: `import { lazy, Suspense } from "react";

const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const CodeEditor = lazy(() => import("./components/CodeEditor"));   // heavy

function App() {
  return (
    <Suspense fallback={<PageSpinner />}>
      <Routes>
        <Route path="/admin" element={<AdminDashboard />} />
      </Routes>
    </Suspense>
  );
}` },
      { type: "ul", items: [
        "Analyse your bundle with a tool such as `source-map-explorer` or `rollup-plugin-visualizer`.",
        "Replace heavy libraries, for example `moment` with `date-fns` or the native `Intl` APIs.",
        "Import only what you need: `import debounce from \"lodash/debounce\"`.",
        "Virtualise long lists, and lazy-load images below the fold."
      ] },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "What is the difference between `useMemo`, `useCallback` and `React.memo`?", answer: ["`React.memo` wraps a component so it skips re-rendering when its props are shallowly equal. `useMemo` caches a computed value between renders until its dependencies change. `useCallback` caches a function reference. `useCallback(fn, deps)` is equivalent to `useMemo(() => fn, deps)`. They are often used together to keep props stable for memoised children."] },
      { type: "qa", question: "Why might `React.memo` not prevent a re-render?", answer: ["Because a prop changes identity every render: an inline object, array or arrow function. The shallow comparison sees a new reference and re-renders. A changed context the component reads will also re-render it."] },
      { type: "qa", question: "Should you wrap everything in `useMemo` and `useCallback`?", answer: ["No. Memoisation has a cost: memory, dependency comparisons and code complexity. Use it when profiling shows an expensive calculation or wasted renders of heavy components, or when you need a stable reference for a memoised child or an effect dependency."] },
      { type: "qa", question: "How do you find performance problems in a React app?", answer: ["Profile a production build with the React DevTools Profiler to find frequently rendering or slow components. Use the Chrome Performance panel for long tasks, Lighthouse and Core Web Vitals for load performance, and a bundle analyser for JavaScript size."] },
      { type: "qa", question: "What do `useTransition` and `useDeferredValue` do?", answer: ["They mark some updates as non-urgent so React can interrupt them to handle urgent ones such as typing. `useTransition` wraps a state update in `startTransition` and gives an `isPending` flag. `useDeferredValue` gives a lagging copy of a value to render expensive UI from."] },
      { type: "qa", question: "What is code splitting?", answer: ["Breaking the JavaScript bundle into smaller chunks that load on demand, typically per route or for heavy components, using dynamic `import()`, `React.lazy` and `Suspense`. It reduces the initial download and speeds up the first load."] },

      { type: "exercise", title: "Fix the slow list", task: "This component re-renders all 2,000 rows whenever the user types in the unrelated note field, and recomputes the sorted list every time. Optimise it without changing the behaviour.", language: "jsx", starter: `function Students({ students }) {
  const [note, setNote] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const sorted = students.toSorted((a, b) => b.score - a.score);

  return (
    <>
      <textarea value={note} onChange={(e) => setNote(e.target.value)} />
      <ul>
        {sorted.map((s) => (
          <Row key={s.id} student={s} selected={s.id === selectedId}
               onSelect={(id) => setSelectedId(id)} />
        ))}
      </ul>
    </>
  );
}

function Row({ student, selected, onSelect }) {
  return <li className={selected ? "active" : ""}
             onClick={() => onSelect(student.id)}>{student.name}</li>;
}`, solution: `function NoteField() {                       // 1. move unrelated state down
  const [note, setNote] = useState("");
  return <textarea value={note} onChange={(e) => setNote(e.target.value)} />;
}

function Students({ students }) {
  const [selectedId, setSelectedId] = useState(null);

  const sorted = useMemo(                     // 2. cache the expensive sort
    () => students.toSorted((a, b) => b.score - a.score),
    [students]
  );

  const handleSelect = useCallback((id) => setSelectedId(id), []);  // 3. stable callback

  return (
    <>
      <NoteField />
      <ul>
        {sorted.map((s) => (
          <Row key={s.id} student={s} selected={s.id === selectedId}
               onSelect={handleSelect} />
        ))}
      </ul>
    </>
  );
}

const Row = memo(function Row({ student, selected, onSelect }) {   // 4. skip unchanged rows
  return <li className={selected ? "active" : ""}
             onClick={() => onSelect(student.id)}>{student.name}</li>;
});`, explanation: "Moving `note` into its own component means typing no longer re-renders the list at all. `useMemo` sorts only when `students` changes. A stable `handleSelect` combined with `memo` means selecting a student re-renders just the two rows whose `selected` prop changed, not all 2,000." },

      { type: "summary", items: [
        "Measure with the React Profiler on a production build before optimising.",
        "Moving state down and lifting content up often beats any memoisation.",
        "`memo` skips re-renders, and `useMemo` and `useCallback` keep values and functions stable.",
        "Use `useDeferredValue` or `useTransition` to keep typing smooth during heavy renders.",
        "Ship less JavaScript with code splitting, lighter libraries and virtualised lists."
      ] }
    ]
  }
];
